import type {Props as IconProps} from '@albert-io/atomic/atoms/Icon/Icon.react';
import Icon from '@albert-io/atomic/atoms/Icon/Icon.react';
import type {Props as TextProps} from '@albert-io/atomic/atoms/Text/Text.react';
import Text from '@albert-io/atomic/atoms/Text/Text.react';
import cx from 'classnames';
import type {ElementType, KeyboardEvent, MouseEvent, ReactElement} from 'react';
import React from 'react';
import {polymorphicForwardRef} from '../../utils/polymorphicForwardRef';
import {useMenuSafe} from './MenuContext.react';

interface ItemProps extends PropsWithChildrenRequired, PropsWithClassNameOptional {
  selected?: boolean;
  disabled?: boolean;
  /**
   * Makes the item non-interactive (no hover states, no click handling)
   * Use this for display-only items vs `disabled` for temporarily unavailable actions
   */
  readonly?: boolean;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  subLabel?: string;
  textProps?: Partial<TextProps>;
  subtextProps?: Partial<TextProps>;
  leftIconProps?: IconProps;
  rightIconProps?: IconProps;
  /**
   * Padding level for the menu item
   *
   * @default 'normal'
   */
  padding?: 'none' | 'normal';
  /**
   * Whether or not the item will `preventDefault` on the click
   * event. You should only ever need to set this to `false` if
   * you need the original click event to propagate to your provided `as`
   * element (ie. `<a>` or `<Link>`).
   */
  preventDefaultClickHandler?: boolean;
  disableHover?: boolean;
  disableActive?: boolean;
}

type MenuItemProps<C extends ElementType> = PolymorphicComponentProps<C, ItemProps>;

type MenuItemComponent = <C extends ElementType = 'div'>(
  props: MenuItemProps<C>
) => ReactElement | null;

const Item = polymorphicForwardRef(
  <C extends ElementType = 'div'>(
    {
      as,
      children,
      className = '',
      selected,
      readonly,
      disabled,
      onClick,
      subLabel,
      textProps,
      subtextProps,
      leftIconProps,
      rightIconProps,
      padding = 'normal',
      preventDefaultClickHandler = true,
      disableHover = false,
      disableActive = false,
      ...rest
    }: MenuItemProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    // Menu context is optional because Menu.Item can be used in a static menu
    const menuContext = useMenuSafe();
    const Component = as || 'div';
    const isInteractive = !disabled && !readonly;

    const handleClick = (e: MouseEvent<HTMLElement>) => {
      if (!isInteractive) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
      if (menuContext?.closeOnItemClick) {
        menuContext?.close();
      }
      if (preventDefaultClickHandler) {
        e.preventDefault();
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
      if (!isInteractive) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        onClick?.(e as unknown as MouseEvent<HTMLElement>);
        if (menuContext?.closeOnItemClick) {
          menuContext?.close();
        }
        if (preventDefaultClickHandler) {
          e.preventDefault();
        }
      }
    };

    const renderContent = () => {
      if (typeof children === 'string') {
        return (
          <div className='flex flex-1 flex-col'>
            <Text size='m' color='inherit' {...textProps}>
              {children}
            </Text>
            {subLabel && (
              <Text color='tertiary' size='xs' {...subtextProps}>
                {subLabel}
              </Text>
            )}
          </div>
        );
      }

      return children;
    };

    return (
      <Component
        className={cx('o-menu__item flex gap-x1', className, {
          'o-menu__item--selected': selected,
          'o-menu__item--disabled': disabled,
          'o-menu__item--readonly': readonly,
          'o-menu__item--no-hover': disableHover,
          'o-menu__item--no-active': disableActive,
          'px-x1-half py-x1': padding === 'normal',
          'cursor-default!': disableActive && disableHover
        })}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-disabled={disabled}
        role='menuitem'
        tabIndex={isInteractive ? 0 : -1}
        ref={ref}
        {...rest}
      >
        {leftIconProps && (
          <div
            className={cx('o-menu__item-icon-container', {
              'self-start pt-x0-half': !!subLabel,
              'self-center': !subLabel
            })}
          >
            <Icon
              iconStyle='regular'
              className='o-menu__item-icon'
              {...leftIconProps}
              emphasis={disabled ? 'disabled' : 'medium'}
            />
          </div>
        )}
        {renderContent()}
        {rightIconProps && (
          <div className='o-menu__item-icon-container self-center'>
            <Icon iconStyle='regular' className='o-menu__item-icon' {...rightIconProps} />
          </div>
        )}
      </Component>
    );
  }
) as unknown as MenuItemComponent;

export default Item;
